import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { MediaToggleDropdown } from '@workspace/ui/components/media-toggle-dropdown'
import { LogOut } from 'lucide-react'
import { useState } from 'react'
import { useSession } from '../../../lib/auth'
import { useMediaDevices } from '../hooks/use-devices'
import { useVideoPreview } from '../hooks/use-video-preview'
import type { PreJoinScreenProps } from '../types/pitch.types'
import { AudioVisualizer } from './audio-visualizer'

export function PreJoinScreen({ onJoin, onCancel, isLoading, error }: PreJoinScreenProps) {
  const { data } = useSession()
  const [audioEnabled, setAudioEnabled] = useState(true)

  const {
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
  } = useMediaDevices()

  const {
    videoRef,
    isEnabled: videoEnabled,
    setEnabled: setVideoEnabled,
    stopPreview,
  } = useVideoPreview(selectedVideoDevice)

  const handleJoin = () => {
    stopPreview()
    onJoin({
      audioEnabled,
      videoEnabled,
      audioDeviceId: selectedAudioDevice || undefined,
      videoDeviceId: selectedVideoDevice || undefined,
    })
  }

  const handleCancel = () => {
    stopPreview()
    onCancel()
  }

  const userInitial = data?.user?.name?.charAt(0).toUpperCase() ?? '?'

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header - same as VideoConference */}
      <div className="flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <img src="/seedforge-logo.svg" alt="SeedForge" className="h-8" />
          <Badge variant="outline" size="lg" className="gap-2 py-2">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-emerald-400">Ready to connect</span>
          </Badge>
        </div>
        <ConfirmButton onConfirm={handleCancel}>
          <LogOut className="size-4" />
          Leave
        </ConfirmButton>
      </div>

      {/* Content centered */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-8">
        <div className="w-full max-w-lg space-y-5">
          {/* Title */}
          <div className="text-center">
            <h2 className="text-lg font-semibold text-white">
              Make sure your audio and video works before joining
            </h2>
          </div>

          {/* Video preview */}
          <div className="aspect-4/3 w-full">
            <div className="relative h-full w-full overflow-hidden rounded-lg bg-card">
              {videoEnabled ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-secondary">
                  <div className="flex size-20 items-center justify-center rounded-full bg-primary">
                    <span className="text-3xl font-semibold text-primary-foreground">
                      {userInitial}
                    </span>
                  </div>
                </div>
              )}

              {/* Media controls centered */}
              <div className="absolute inset-x-0 bottom-4 flex justify-center gap-3">
                <MediaToggleDropdown
                  type="audio"
                  enabled={audioEnabled}
                  onToggle={() => setAudioEnabled(!audioEnabled)}
                  devices={audioDevices}
                  selectedDeviceId={selectedAudioDevice}
                  onDeviceSelect={setSelectedAudioDevice}
                  size="lg"
                />
                <MediaToggleDropdown
                  type="video"
                  enabled={videoEnabled}
                  onToggle={() => setVideoEnabled(!videoEnabled)}
                  devices={videoDevices}
                  selectedDeviceId={selectedVideoDevice}
                  onDeviceSelect={setSelectedVideoDevice}
                  size="lg"
                />
              </div>
            </div>
          </div>

          {/* Audio visualizer */}
          <div className="h-10 w-full">
            {audioEnabled ? (
              <AudioVisualizer deviceId={selectedAudioDevice} enabled={audioEnabled} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-xs text-muted-foreground">Microphone is muted</span>
              </div>
            )}
          </div>

          {/* Join */}
          {error && <p className="text-center text-sm text-red-400">{error}</p>}
          <Button onClick={handleJoin} disabled={isLoading || !audioEnabled} className="w-full">
            {isLoading
              ? 'Joining...'
              : !audioEnabled
                ? 'Enable microphone to join'
                : 'Join Session'}
          </Button>
        </div>
      </div>
    </div>
  )
}
